import classNames from 'classnames';
import Text from 'components/Text/Text';
import React from 'react';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './DraggableSelectedItem.module.scss';
import { DraggableProvided } from 'react-beautiful-dnd';

interface IDraggableSelectedItem {
	draggableInnerRef: ((element?: HTMLElement | null | undefined) => any) | undefined;
	provided: DraggableProvided;
	positionNumber: number;
	label: string | string[];
	onDelete: () => void;
	separator?: string | JSX.Element;
	hasDragAccess?: boolean;
	additionalContent?: JSX.Element;
}

const DraggableSelectedItem: React.FC<IDraggableSelectedItem> = ({
	draggableInnerRef,
	provided,
	positionNumber,
	label,
	onDelete,
	separator,
	hasDragAccess = true,
	additionalContent,
}) => {
	const positionContainerClasses = classNames(style.positionContainer, { [style.hidden]: !hasDragAccess });

	const defaultSeparator = () => (
		<svg className={style.separatorIcon}>
			<use xlinkHref={`${breadcrumbs}#rightAngleArrow`} />
		</svg>
	);

	const isStringSeparator = typeof separator === 'string';

	const separatorElement = isStringSeparator ? <span className={style.separator}>{separator}</span> : separator;

	const singleContent = () => (
		<Text translationKey={label as string} theme="secondary" size="xs" withTooltip={true} />
	);

	const multipleContent = () => (
		<div className={style.labelRow}>
			{Array.isArray(label) &&
				label.map((el, i) => {
					const lastItem = i === label.length - 1;
					return (
						<React.Fragment key={el}>
							<Text translationKey={el} theme="secondary" size="s" withTooltip={true} />
							{!lastItem && (separator ? separatorElement : defaultSeparator())}
						</React.Fragment>
					);
				})}
		</div>
	);

	return (
		<div
			className={style.container}
			ref={hasDragAccess ? draggableInnerRef : undefined}
			{...provided.draggableProps}
			data-test="DraggableSelectedItem">
			<div className={positionContainerClasses}>
				<Text
					className={style.positionNumber}
					translationNameSpace="form"
					translationKey="positionNumberLabel"
					translationOptions={{ positionNumber }}
					theme="secondary"
					size="s"
					weight="bold"
				/>
				<div className={style.dragMark} {...provided.dragHandleProps}>
					<svg>
						<use xlinkHref={`${breadcrumbs}#dragMark`} />
					</svg>
				</div>
			</div>
			<div className={style.content}>
				{Array.isArray(label) ? multipleContent() : singleContent()}
				{additionalContent}
			</div>
			<div className={style.deleteBtn} onClick={onDelete}>
				<svg className={style.deleteIcon}>
					<use xlinkHref={`${breadcrumbs}#closeSelectItem`} />
				</svg>
			</div>
		</div>
	);
};

export default React.memo(DraggableSelectedItem);
